import { create } from "zustand";
import {
  TabId,
  type SearchHit,
  type AccountScope,
} from "./types";

// V2 — per-tab result slice
export interface TabResultsState<T = unknown> {
  hits: T[];
  loading: boolean;
  nextCursor: string | null;
}

const makeEmptyTabState = (): TabResultsState => ({
  hits: [],
  loading: false,
  nextCursor: null,
});

const makeEmptyResultsByTab = (): Record<TabId, TabResultsState> => ({
  [TabId.chats]:    makeEmptyTabState(),
  [TabId.channels]: makeEmptyTabState(),
  [TabId.people]:   makeEmptyTabState(),
  [TabId.messages]: makeEmptyTabState(),
  [TabId.links]:    makeEmptyTabState(),
});

export interface SearchStore {
  // Global search input value (used by GlobalSearchInput)
  query: string;
  setQuery: (q: string) => void;

  // V2 global search — scope + per-tab slices
  scope: AccountScope;
  activeTab: TabId;
  resultsByTab: Record<TabId, TabResultsState>;
  setScope: (s: AccountScope) => void;
  setActiveTab: (t: TabId) => void;
  setTabResults: <T>(tab: TabId, partial: Partial<TabResultsState<T>>) => void;
  resetTabs: () => void;

  // In-chat search
  inChatActive: boolean;
  inChatQuery: string;
  inChatResults: SearchHit[];
  inChatLoading: boolean;
  inChatIndex: number;        // current hit index (0-based) for highlight + scroll
  inChatPanelOpen: boolean;   // results panel visible — driven by input focus
  setInChatActive: (b: boolean) => void;
  setInChatQuery: (q: string) => void;
  setInChatResults: (hits: SearchHit[]) => void;
  setInChatLoading: (b: boolean) => void;
  setInChatPanelOpen: (b: boolean) => void;
  nextHit: () => void;
  prevHit: () => void;

  // Jump target so MessageList can load context + scroll
  jumpTargetMessageId: string | null;
  jumpRequestNonce: number;
  requestJumpTo: (messageId: string) => void;
  clearJumpTarget: () => void;

  reset: () => void;
}

export const useSearchStore = create<SearchStore>((set, get) => ({
  query: "",
  setQuery: (q) => set({ query: q }),

  scope: { kind: "account", id: "" },
  activeTab: TabId.chats,
  resultsByTab: makeEmptyResultsByTab(),
  setScope: (s) => set({ scope: s }),
  setActiveTab: (t) => set({ activeTab: t }),
  setTabResults: (tab, partial) => set((s) => ({
    resultsByTab: {
      ...s.resultsByTab,
      [tab]: { ...s.resultsByTab[tab], ...partial } as TabResultsState,
    },
  })),
  resetTabs: () => set({ resultsByTab: makeEmptyResultsByTab() }),

  inChatActive: false,
  inChatQuery: "",
  inChatResults: [],
  inChatLoading: false,
  inChatIndex: 0,
  inChatPanelOpen: false,
  setInChatActive: (b) => set({ inChatActive: b }),
  setInChatQuery: (q) => set({ inChatQuery: q }),
  setInChatResults: (hits) => set({ inChatResults: hits, inChatIndex: 0 }),
  setInChatLoading: (b) => set({ inChatLoading: b }),
  setInChatPanelOpen: (b) => set({ inChatPanelOpen: b }),
  nextHit: () => {
    const { inChatResults, inChatIndex } = get();
    if (inChatResults.length === 0) return;
    set({ inChatIndex: (inChatIndex + 1) % inChatResults.length });
  },
  prevHit: () => {
    const { inChatResults, inChatIndex } = get();
    if (inChatResults.length === 0) return;
    set({ inChatIndex: (inChatIndex - 1 + inChatResults.length) % inChatResults.length });
  },

  jumpTargetMessageId: null,
  jumpRequestNonce: 0,
  requestJumpTo: (messageId) => set((s) => ({
    jumpTargetMessageId: messageId,
    jumpRequestNonce: s.jumpRequestNonce + 1,
  })),
  clearJumpTarget: () => set({ jumpTargetMessageId: null }),

  reset: () => set({
    query: "",
    inChatActive: false, inChatQuery: "", inChatResults: [], inChatLoading: false,
    inChatIndex: 0, inChatPanelOpen: false,
    jumpTargetMessageId: null,
  }),
}));
